/**
 * User Settings V2 — "Communication" section: additional email
 * addresses and, for legacy users only, the retiring SMS number list.
 *
 * Contains:
 * - CommunicationSection — the rendered SettingsSection (default export)
 */

import ContactMailIcon from '@mui/icons-material/ContactMail';
import { Alert, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsListField, SettingsSection, SettingsSubsection } from '@sophocles/views/settings/components';

// Communication channels — multi-row editors for additional email
// addresses (and a legacy view of any SMS numbers the user already
// has on file).  SMS is being retired; we don't allow new SMS
// enrollment, only management of existing entries.
export default function CommunicationSection({ hasLegacySms }) {
  const { t } = useTranslation();
  return (
    <SettingsSection
      id="settings-communication"
      icon={<ContactMailIcon />}
      title={t('userSettings.v2.communication', 'Communication')}
      description={t(
        'userSettings.v2.communicationDescription',
        'Additional email addresses that receive notifications alongside your account email.',
      )}
    >
      <Stack spacing={3}>
        <SettingsSubsection
          title={t('userSettings.additionalEmails', 'Additional Email Addresses')}
          description={t(
            'userSettings.additionalEmailsDescription',
            'Notifications and alerts will also be sent to these addresses.',
          )}
        >
          <SettingsListField
            name="contact.email"
            type="email"
            itemLabel={t('general.emailAddress', 'Email Address')}
            placeholder="user@example.com"
            addLabel={t('userSettings.addEmail', 'Add Email Address')}
            emptyLabel={t('userSettings.noAdditionalEmails', 'No additional email addresses.')}
          />
        </SettingsSubsection>
        {hasLegacySms ? (
          <SettingsSubsection
            title={t('userSettings.additionalSms', 'SMS Numbers')}
            description={t(
              'userSettings.v2.smsLegacyDescription',
              'Existing SMS numbers on your account. SMS notifications are being retired — you can remove numbers but cannot add new ones.',
            )}
          >
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'userSettings.smsDeprecationWarning',
                'SMS notifications are being retired and will be removed in a future update. Please switch to an alternative notification method.',
              )}
            </Alert>
            <SettingsListField
              name="contact.sms"
              type="tel"
              itemLabel={t('userSettings.phoneNumber', 'Phone Number')}
              addLabel={null}
              emptyLabel=""
              readOnly
            />
          </SettingsSubsection>
        ) : null}
      </Stack>
    </SettingsSection>
  );
}
